import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaIO } from '@frontend/schema';
import { SchemaSMSDataV3Service, serviceName } from '../service';
import { LookupThreadIdIO, ServiceQueries } from '../types';
import { useSMSDataV3Queries } from './use-sms-data-v3-queries';
import { UseSMSDataV3QueryEndpointArgs, useSMSDataV3Query } from './use-sms-data-v3-query';

/**
 * A hook that returns a query for the `LookupThreadId` query endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useLookupThreadIdQuery = <E = unknown, D = SchemaIO<ServiceQueries['LookupThreadId']>['output']>(
  args: UseSMSDataV3QueryEndpointArgs<'LookupThreadId', E, D>
) =>
  useSMSDataV3Query({
    endpointName: 'LookupThreadId',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `LookupThreadId` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 */
export const useLookupThreadIdQueries = <E = unknown, D = SchemaIO<ServiceQueries['LookupThreadId']>['output']>(
  argsArr: UseSMSDataV3QueryEndpointArgs<'LookupThreadId', E, D>[]
) =>
  useSMSDataV3Queries(
    argsArr.map((args) => ({
      endpointName: 'LookupThreadId',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `LookupThreadId` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchLookupThreadId = () => {
  const queryClient = useQueryClient();

  const fetchLookupThreadId = async <E = unknown, D = LookupThreadIdIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSMSDataV3QueryEndpointArgs<'LookupThreadId', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      LookupThreadIdIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'LookupThreadId'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        LookupThreadIdIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'LookupThreadId'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'LookupThreadId'>({
          serviceName,
          endpointName: 'LookupThreadId',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSMSDataV3Service.LookupThreadId(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchLookupThreadId;
};
